import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Truck, Package, Mail, Phone, MapPin,
  Receipt, AlertTriangle, Send, ExternalLink, Banknote, RefreshCcw, FileText,
  CheckCircle2, Clock,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_FLOW = ["pending", "paid", "shipped", "completed", "refunded", "cancelled"];

const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);
  const [tracking, setTracking] = useState({ carrier: "", tracking_code: "", tracking_url: "" });
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundAmt, setRefundAmt] = useState("");
  const [confirmRefund, setConfirmRefund] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, cRes] = await Promise.all([
        api.get(`/admin/orders/${id}`),
        api.get(`/shop/carriers`),
      ]);
      setOrder(oRes.data);
      setCarriers(cRes.data);
      setTracking({
        carrier: oRes.data.carrier || "",
        tracking_code: oRes.data.tracking_code || "",
        tracking_url: oRes.data.tracking_url || "",
      });
      setAdminNotes(oRes.data.admin_notes || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const liveTrackingUrl = useMemo(() => {
    if (tracking.carrier === "other") return tracking.tracking_url;
    const c = carriers.find((x) => x.key === tracking.carrier);
    if (!c || !tracking.tracking_code) return "";
    // Mirror server-side template logic for instant preview
    const tmpl = ({
      nz_post: "https://www.nzpost.co.nz/tools/tracking?trackid={code}",
      nz_couriers: "https://www.nzcouriers.co.nz/track-and-trace?con_no={code}",
      post_haste: "https://www.posthaste.co.nz/track-and-trace?con_no={code}",
      castle_parcels: "https://www.castleparcels.co.nz/track-and-trace?con_no={code}",
      aramex: "https://track.aramex.co.nz/?l={code}",
      gss: "https://www.gosweetspot.com/Tracking?TrackingNumber={code}",
      courier_post: "https://www.nzpost.co.nz/tools/tracking/item?trackid={code}",
    })[tracking.carrier];
    return tmpl ? tmpl.replace("{code}", tracking.tracking_code) : "";
  }, [tracking, carriers]);

  const saveTracking = async (markShipped = false) => {
    if (markShipped && !tracking.tracking_code) {
      toast.error("Add a tracking code first");
      return;
    }
    setShipping(markShipped);
    setSaving(!markShipped);
    try {
      const payload = {
        carrier: tracking.carrier || null,
        tracking_code: tracking.tracking_code || null,
        tracking_url: tracking.carrier === "other" ? tracking.tracking_url : null,
      };
      if (markShipped) payload.status = "shipped";
      const res = await api.patch(`/admin/orders/${id}`, payload);
      setOrder(res.data);
      toast.success(markShipped ? "Order marked shipped" : "Tracking saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
      setShipping(false);
    }
  };

  const setStatus = async (status) => {
    try {
      const res = await api.patch(`/admin/orders/${id}`, { status });
      setOrder(res.data);
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/orders/${id}`, { admin_notes: adminNotes });
      setOrder(res.data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const emailTracking = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/admin/orders/${id}/email-tracking`);
      toast.success(`Emailed ${res.data.to}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const doRefund = async () => {
    setRefunding(true);
    try {
      const params = refundAmt ? { amount: Number(refundAmt) } : {};
      const res = await api.post(`/admin/orders/${id}/refund`, null, { params });
      toast.success(`Refunded ${money(res.data.refunded)}`);
      setConfirmRefund(false);
      setRefundAmt("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  if (loading) {
    return <AdminLayout title="Order"><div className="p-12 text-center"><Loader2 className="size-6 animate-spin inline text-spark-yellow" /></div></AdminLayout>;
  }
  if (!order) {
    return <AdminLayout title="Order"><div className="p-12 text-center text-slate-400">Order not found.</div></AdminLayout>;
  }

  const remaining = Number(order.total) - Number(order.refunded_amount || 0);
  const canRefund = !!order.stripe_payment_intent && remaining > 0;

  return (
    <AdminLayout
      title={order.order_number}
      eyebrow="Shop · Order"
      actions={
        <Link to="/admin/shop" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider font-bold bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10" data-testid="back-to-orders">
          <ArrowLeft className="size-3.5" /> All orders
        </Link>
      }
    >
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5" data-testid="admin-order-detail">

        {/* Left: details */}
        <div className="lg:col-span-2 space-y-4">

          {/* Header summary */}
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Status</p>
                <p className="font-display uppercase tracking-tight text-3xl font-bold text-spark-yellow mt-0.5" data-testid="order-status">{order.status}</p>
                <p className="text-xs text-slate-400 mt-1">Placed {new Date(order.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Total</p>
                <p className="font-display uppercase tracking-tight text-3xl font-bold text-white">{money(order.total)}</p>
                {order.refunded_amount > 0 && <p className="text-xs text-orange-300 mt-0.5">−{money(order.refunded_amount)} refunded</p>}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {STATUS_FLOW.filter((s) => s !== order.status).map((s) => (
                <button key={s} onClick={() => setStatus(s)} className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 text-[10px] uppercase tracking-wider font-bold" data-testid={`set-status-${s}`}>
                  Mark {s}
                </button>
              ))}
            </div>
          </div>

          {/* Items */}
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5 sm:p-6">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-3">Items</p>
            <ul className="divide-y divide-white/[0.06]">
              {order.items.map((it, i) => (
                <li key={i} className="py-2.5 flex items-start justify-between gap-3 text-sm">
                  <div>
                    <p className="text-white font-semibold">{it.name}</p>
                    <p className="text-[11px] text-slate-500 font-mono">{it.sku} · qty {it.quantity}</p>
                  </div>
                  <p className="text-white font-display uppercase tabular-nums">{money(it.price * it.quantity)}</p>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1 text-sm">
              <div className="flex justify-between text-slate-300"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.shipping_total > 0 && <div className="flex justify-between text-slate-300"><span>Shipping</span><span>{money(order.shipping_total)}</span></div>}
              <div className="flex justify-between text-white font-display uppercase tracking-wider font-bold pt-2 border-t border-white/[0.06]"><span>Total</span><span>{money(order.total)}</span></div>
            </div>
          </div>

          {/* Tracking */}
          <div className={`rounded-xl border p-5 sm:p-6 ${order.fulfilment === "shipping" || order.tracking_code ? "border-spark-teal/30 bg-spark-teal/5" : "border-white/[0.07] bg-white/[0.04]"}`} data-testid="order-tracking-card">
            <div className="flex items-center gap-2 mb-3">
              <Truck className={`size-4 ${order.fulfilment === "shipping" || order.tracking_code ? "text-spark-teal" : "text-slate-500"}`} />
              <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${order.fulfilment === "shipping" || order.tracking_code ? "text-spark-teal" : "text-slate-400"}`}>Shipping &amp; tracking</p>
              {order.fulfilment === "pickup" && (
                <span className="ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/10 text-slate-300">Pickup order</span>
              )}
            </div>
            {order.fulfilment === "pickup" && !order.tracking_code ? (
              <div className="text-sm text-slate-400 leading-relaxed">
                <p>This order is set for <strong className="text-white">workshop pickup</strong>, so tracking isn't normally needed.</p>
                <p className="mt-1.5 text-xs">If you do end up shipping it (e.g. the customer changed their mind), you can add a tracking code below and it'll show up on their dashboard automatically.</p>
              </div>
            ) : null}
            <div className={`grid sm:grid-cols-2 gap-3 ${order.fulfilment === "pickup" && !order.tracking_code ? "mt-4 pt-4 border-t border-white/5" : ""}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Courier</label>
                  <select className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm" value={tracking.carrier} onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })} data-testid="tracking-carrier">
                    <option value="" className="bg-spark-teal-night">— Select courier —</option>
                    {carriers.map((c) => (
                      <option key={c.key} value={c.key} className="bg-spark-teal-night text-white">{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Tracking number</label>
                  <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm font-mono" value={tracking.tracking_code} onChange={(e) => setTracking({ ...tracking, tracking_code: e.target.value })} placeholder="e.g. EZ123456789NZ" data-testid="tracking-code" />
                </div>
                {tracking.carrier === "other" && (
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Custom tracking URL</label>
                    <input className="mt-1.5 w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm" value={tracking.tracking_url} onChange={(e) => setTracking({ ...tracking, tracking_url: e.target.value })} placeholder="https://…" data-testid="tracking-url" />
                  </div>
                )}
              </div>
              {liveTrackingUrl && (
                <a href={liveTrackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-spark-teal hover:text-spark-yellow text-xs font-semibold" data-testid="tracking-preview-link">
                  <ExternalLink className="size-3.5" /> Open live tracking page
                </a>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => saveTracking(false)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 hover:bg-white/15 text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50" data-testid="tracking-save">
                  {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} Save
                </button>
                {order.status !== "shipped" && order.status !== "completed" && (
                  <button onClick={() => saveTracking(true)} disabled={shipping || !tracking.tracking_code} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-spark-teal text-white hover:bg-spark-teal-deep text-xs uppercase tracking-wider font-bold disabled:opacity-40" data-testid="tracking-save-ship">
                    {shipping ? <Loader2 className="size-3.5 animate-spin" /> : <Truck className="size-3.5" />} Save &amp; mark shipped
                  </button>
                )}
                <button onClick={emailTracking} disabled={emailing || !order.tracking_code} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-spark-yellow text-spark-ink hover:bg-yellow-400 text-xs uppercase tracking-wider font-bold disabled:opacity-40" data-testid="tracking-email">
                  {emailing ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />} Email tracking to customer
                </button>
              </div>
              {order.tracking_emailed_at && (
                <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5"><CheckCircle2 className="size-3.5 text-emerald-400" /> Last emailed {new Date(order.tracking_emailed_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}</p>
              )}
            </div>

          {/* Admin notes */}
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5 sm:p-6">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-2">Admin notes <span className="text-slate-500 normal-case tracking-normal">(private — never shown to customer)</span></p>
            <textarea rows={3} className="w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="e.g. Called customer on 2pm, awaiting return label." data-testid="admin-notes" />
            <button onClick={saveNotes} disabled={saving} className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 hover:bg-white/15 text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50" data-testid="save-notes">
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} Save notes
            </button>
          </div>
        </div>

        {/* Right: customer + payment */}
        <div className="space-y-4">

          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-3">Customer</p>
            <p className="text-white font-semibold">{order.customer_name}</p>
            <a href={`mailto:${order.customer_email}`} className="text-sm text-spark-teal hover:text-spark-yellow inline-flex items-center gap-1.5 mt-1.5"><Mail className="size-3.5" /> {order.customer_email}</a>
            {order.customer_phone && (
              <a href={`tel:${order.customer_phone.replace(/[^+\d]/g,'')}`} className="text-sm text-spark-teal hover:text-spark-yellow inline-flex items-center gap-1.5 mt-1.5 ml-3"><Phone className="size-3.5" /> {order.customer_phone}</a>
            )}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
                {order.fulfilment === "shipping" ? <Truck className="size-3" /> : <Package className="size-3" />}
                {order.fulfilment === "shipping" ? "Shipping" : "Pickup"}
              </p>
              {order.fulfilment === "shipping" && order.shipping_address && (
                <p className="text-sm text-white mt-1.5 flex items-start gap-1.5"><MapPin className="size-3.5 mt-0.5 text-spark-teal shrink-0" /> {order.shipping_address}</p>
              )}
              {order.notes && (
                <p className="text-xs text-slate-300 italic mt-2">"{order.notes}"</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-3">Payment</p>
            <p className="font-display uppercase tracking-tight text-2xl font-bold text-white">{money(order.total)}</p>
            <p className="text-xs text-slate-400 mt-0.5">Status: <span className="text-emerald-300 font-semibold">{order.payment_status}</span></p>
            {order.stripe_payment_intent && (
              <p className="text-[10px] text-slate-500 font-mono mt-2 break-all">PI: {order.stripe_payment_intent}</p>
            )}
            {order.refunded_amount > 0 && (
              <p className="text-xs text-orange-300 mt-2">Refunded: {money(order.refunded_amount)}</p>
            )}
            <div className="mt-4 space-y-2">
              <input type="number" step="0.01" placeholder={`Refund amount (max ${money(remaining)})`} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} disabled={!canRefund} className="w-full bg-spark-teal-night border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-slate-500 disabled:opacity-40" data-testid="refund-amt" />
              <button onClick={() => setConfirmRefund(true)} disabled={!canRefund} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 text-xs uppercase tracking-wider font-bold disabled:opacity-40" data-testid="open-refund">
                <RefreshCcw className="size-3.5" /> {refundAmt ? `Refund ${money(refundAmt)}` : `Refund ${money(remaining)} (full)`}
              </button>
              {!order.stripe_payment_intent && (
                <p className="text-[10px] text-slate-500 italic">No Stripe payment found. Refund manually if needed.</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-3">Quick links</p>
            <button onClick={() => navigator.clipboard.writeText(window.location.href).then(() => toast.success("Link copied"))} className="w-full text-left text-sm text-slate-300 hover:text-white py-1.5 inline-flex items-center gap-2"><FileText className="size-3.5" /> Copy admin link</button>
            <a href={`mailto:${order.customer_email}?subject=Re: Order ${order.order_number}`} className="block text-sm text-slate-300 hover:text-white py-1.5 inline-flex items-center gap-2"><Mail className="size-3.5" /> Email customer</a>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRefund}
        title={refundAmt ? "Issue partial refund?" : "Issue full refund?"}
        body={
          <>
            <p>This will trigger a Stripe refund of <strong className="text-white">{money(refundAmt || remaining)}</strong> back to <strong>{order.customer_name}</strong>'s card.</p>
            <p className="text-slate-400">Stripe processes the refund instantly. The customer's bank may take 5–10 days to display it.</p>
          </>
        }
        confirmLabel={refundAmt ? `Refund ${money(refundAmt)}` : "Refund full amount"}
        busy={refunding}
        onConfirm={doRefund}
        onCancel={() => !refunding && setConfirmRefund(false)}
        testId="confirm-refund"
      />
    </AdminLayout>
  );
};

export default AdminOrderDetail;
